import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Users, Calendar, FileText, Bell, Search, RefreshCw, ArrowLeft,
  CheckCircle2, Clock, AlertTriangle, Stethoscope, Activity, Pill,
  ClipboardList, Phone, Heart, Eye
} from "lucide-react";

// Mock data for physician portal
const MOCK_PATIENTS = [
  { id: "P001", name: "Mary Johnson", status: "active", diagnosis: "End-stage COPD", lastF2F: "2026-01-10", nextRecert: "2026-02-10" },
  { id: "P002", name: "Robert Smith", status: "active", diagnosis: "Metastatic Lung Cancer", lastF2F: "2026-01-05", nextRecert: "2026-02-05" },
  { id: "P003", name: "James Brown", status: "active", diagnosis: "Advanced Dementia", lastF2F: "2025-12-20", nextRecert: "2026-01-25" },
  { id: "P004", name: "Patricia Davis", status: "active", diagnosis: "End-stage Renal Disease", lastF2F: "2026-01-12", nextRecert: "2026-02-12" },
];

const MOCK_ORDERS = [
  { id: "O001", patient: "Mary Johnson", type: "Medication Change", status: "pending_signature", created: "2026-01-18", medication: "Morphine 10mg" },
  { id: "O002", patient: "Robert Smith", type: "DME Order", status: "pending_review", created: "2026-01-17", item: "Oxygen Concentrator" },
  { id: "O003", patient: "James Brown", type: "Lab Order", status: "pending_signature", created: "2026-01-16", test: "CBC, CMP" },
];

const MOCK_CERTIFICATIONS = [
  { id: "C001", patient: "James Brown", type: "Recertification", dueDate: "2026-01-25", status: "due_soon" },
  { id: "C002", patient: "Mary Johnson", type: "F2F", dueDate: "2026-02-10", status: "scheduled" },
];

const MOCK_MESSAGES = [
  { id: "M001", from: "Jennifer Adams, RN", subject: "Mary Johnson - Pain Management", date: "2026-01-18", unread: true },
  { id: "M002", from: "Lisa Thompson, SW", subject: "Family Meeting Request - Robert Smith", date: "2026-01-17", unread: true },
  { id: "M003", from: "IDG Team", subject: "Weekly IDG Summary", date: "2026-01-15", unread: false },
];

export default function PhysicianPortal() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("patients");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Data
  const [patients, setPatients] = useState(MOCK_PATIENTS);
  const [orders, setOrders] = useState(MOCK_ORDERS);
  const [certifications, setCertifications] = useState(MOCK_CERTIFICATIONS);
  const [messages, setMessages] = useState(MOCK_MESSAGES);
  
  // Dialogs
  const [showOrdersDialog, setShowOrdersDialog] = useState(false);
  const [showCertsDialog, setShowCertsDialog] = useState(false);
  const [showMessagesDialog, setShowMessagesDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Try to load real data, fall back to mock
      const [patientsRes, ordersRes] = await Promise.all([
        api.get("/physician/patients").catch(() => ({ data: { patients: MOCK_PATIENTS } })),
        api.get("/physician/orders").catch(() => ({ data: { orders: MOCK_ORDERS } }))
      ]);
      
      setPatients(patientsRes.data?.patients || MOCK_PATIENTS);
      setOrders(ordersRes.data?.orders || MOCK_ORDERS);
    } catch (error) {
      console.error("Load error:", error);
    } finally {
      setLoading(false);
    }
  };

  const signOrder = (orderId) => {
    setOrders(orders.map(o => 
      o.id === orderId ? { ...o, status: 'signed' } : o
    ));
    toast.success("Order signed successfully");
  };

  const filteredPatients = patients.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.diagnosis?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingSignatures = orders.filter(o => o.status === 'pending_signature').length;
  const unreadMessages = messages.filter(m => m.unread).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50" data-testid="physician-portal">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="text-white hover:bg-white/10"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
                <Stethoscope className="h-8 w-8" />
                Physician Portal
              </h1>
              <p className="text-teal-100 mt-1">Manage hospice patients and sign orders</p>
            </div>
            <Button onClick={loadData} className="bg-white/10 hover:bg-white/20">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveTab('patients')}
            data-testid="stat-patients"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-teal-700">{patients.length}</p>
                  <p className="text-sm text-muted-foreground">My Patients</p>
                </div>
                <Users className="w-8 h-8 text-teal-500" />
              </div>
              <p className="text-xs text-teal-500 mt-2">View all patients →</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer hover:shadow-lg transition-shadow ${pendingSignatures > 0 ? "border-amber-200 bg-amber-50" : ""}`}
            onClick={() => setShowOrdersDialog(true)}
            data-testid="stat-orders"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-amber-700">{pendingSignatures}</p>
                  <p className="text-sm text-muted-foreground">Pending Signatures</p>
                </div>
                <ClipboardList className="w-8 h-8 text-amber-500" />
              </div>
              <p className="text-xs text-amber-500 mt-2">Sign orders →</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer hover:shadow-lg transition-shadow ${certifications.filter(c => c.status === 'due_soon').length > 0 ? "border-red-200 bg-red-50" : ""}`}
            onClick={() => setShowCertsDialog(true)}
            data-testid="stat-certs"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-red-700">{certifications.length}</p>
                  <p className="text-sm text-muted-foreground">Certifications Due</p>
                </div>
                <Calendar className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-xs text-red-500 mt-2">View certifications →</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer hover:shadow-lg transition-shadow ${unreadMessages > 0 ? "border-blue-200 bg-blue-50" : ""}`}
            onClick={() => setShowMessagesDialog(true)}
            data-testid="stat-messages"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-700">{unreadMessages}</p>
                  <p className="text-sm text-muted-foreground">Unread Messages</p>
                </div>
                <Bell className="w-8 h-8 text-blue-500" />
              </div>
              <p className="text-xs text-blue-500 mt-2">View messages →</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="patients"><Users className="w-4 h-4 mr-2" />Patients</TabsTrigger>
            <TabsTrigger value="orders"><ClipboardList className="w-4 h-4 mr-2" />Orders</TabsTrigger>
            <TabsTrigger value="f2f"><Calendar className="w-4 h-4 mr-2" />F2F Schedule</TabsTrigger>
          </TabsList>

          <TabsContent value="patients">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Hospice Patients</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search patients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {filteredPatients.map(patient => (
                    <div key={patient.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div>
                        <p className="font-medium">{patient.name}</p>
                        <p className="text-sm text-muted-foreground">{patient.diagnosis}</p>
                        <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                          <span>Last F2F: {patient.lastF2F}</span>
                          <span>Next Recert: {patient.nextRecert}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className="bg-green-100 text-green-800">{patient.status}</Badge>
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4 mr-1" />
                          View Chart
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>Pending Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {orders.map(order => (
                    <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div>
                        <p className="font-medium">{order.patient}</p>
                        <p className="text-sm text-muted-foreground">{order.type}</p>
                        <p className="text-xs text-muted-foreground">
                          {order.medication || order.item || order.test}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={
                          order.status === 'signed' ? 'bg-green-100 text-green-800' :
                          order.status === 'pending_signature' ? 'bg-amber-100 text-amber-800' :
                          'bg-blue-100 text-blue-800'
                        }>
                          {order.status.replace('_', ' ')}
                        </Badge>
                        {order.status === 'pending_signature' && (
                          <Button size="sm" onClick={() => signOrder(order.id)}>
                            Sign Order
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="f2f">
            <Card>
              <CardHeader>
                <CardTitle>Face-to-Face Schedule</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {certifications.map(cert => (
                    <div key={cert.id} className={`flex items-center justify-between p-4 border rounded-lg ${
                      cert.status === 'due_soon' ? 'border-red-200 bg-red-50' : 'hover:bg-slate-50'
                    }`}>
                      <div>
                        <p className="font-medium">{cert.patient}</p>
                        <p className="text-sm text-muted-foreground">{cert.type}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-medium">{cert.dueDate}</p>
                          <Badge className={cert.status === 'due_soon' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                            {cert.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <Button size="sm" variant="outline">Schedule</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Orders Dialog */}
      <Dialog open={showOrdersDialog} onOpenChange={setShowOrdersDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Pending Orders for Signature</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {orders.filter(o => o.status === 'pending_signature').map((order, idx) => (
              <div key={idx} className="p-4 border rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium">{order.patient}</p>
                  <p className="text-sm text-muted-foreground">{order.type}: {order.medication || order.item || order.test}</p>
                </div>
                <Button size="sm" onClick={() => signOrder(order.id)}>Sign</Button>
              </div>
            ))}
            {orders.filter(o => o.status === 'pending_signature').length === 0 && (
              <p className="text-center text-muted-foreground py-8">No pending orders</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Certifications Dialog */}
      <Dialog open={showCertsDialog} onOpenChange={setShowCertsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upcoming Certifications</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {certifications.map((cert, idx) => (
              <div key={idx} className={`p-4 border rounded-lg ${cert.status === 'due_soon' ? 'bg-red-50 border-red-200' : ''}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{cert.patient}</p>
                    <p className="text-sm text-muted-foreground">{cert.type}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{cert.dueDate}</p>
                    <Badge className={cert.status === 'due_soon' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                      {cert.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Messages Dialog */}
      <Dialog open={showMessagesDialog} onOpenChange={setShowMessagesDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Messages</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {messages.map((msg, idx) => (
              <div key={idx} className={`p-4 border rounded-lg ${msg.unread ? 'bg-blue-50 border-blue-200' : ''}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{msg.subject}</p>
                    <p className="text-sm text-muted-foreground">From: {msg.from}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">{msg.date}</p>
                    {msg.unread && <Badge className="bg-blue-100 text-blue-800">Unread</Badge>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
